/**
 * Delivery zone checker — MVP
 * Checks delivery + pickup via Zapiet APIs; map via OpenStreetMap (default) or Google Maps.
 */
(function () {
  const ROOT_SELECTOR = '[data-wonderly-delivery-zone]';
  const MAP_CALLBACK = '_wonderly_dz_maps_cb';
  const LEAFLET_VERSION = '1.9.4';
  const PIN_PATH =
    'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z';

  function waitForZapietConfig(timeoutMs) {
    const timeout = timeoutMs || 20000;
    const start = Date.now();

    return new Promise(function (resolve, reject) {
      function poll() {
        const config =
          window.ZapietCachedSettings && window.ZapietCachedSettings.cached_config;

        if (config && window.Shopify && window.Shopify.shop) {
          resolve(config);
          return;
        }

        if (Date.now() - start > timeout) {
          reject(
            new Error(
              'Zapiet did not load. Enable the Zapiet app embed in Theme settings → App embeds.'
            )
          );
          return;
        }

        requestAnimationFrame(poll);
      }

      poll();
    });
  }

  function decodeGmak(config) {
    if (!config || !config.gmak) return null;

    try {
      return atob(config.gmak);
    } catch (e) {
      return null;
    }
  }

  function isLocalDevHost() {
    const host = window.location.hostname;
    return host === '127.0.0.1' || host === 'localhost';
  }

  function hasMerchantGoogleKey(root) {
    return !!((root && root.dataset.googleMapsApiKey) || '').trim();
  }

  function getMapsApiKey(root, config) {
    const override = (root && root.dataset.googleMapsApiKey) || '';
    if (override.trim()) return override.trim();
    return decodeGmak(config);
  }

  function loadLeaflet() {
    if (window.L && window.L.map) {
      return Promise.resolve(window.L);
    }

    if (window._wonderlyDzLeafletLoading) {
      return window._wonderlyDzLeafletLoading;
    }

    window._wonderlyDzLeafletLoading = new Promise(function (resolve, reject) {
      if (!document.querySelector('link[data-wonderly-dz-leaflet]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href =
          'https://unpkg.com/leaflet@' +
          LEAFLET_VERSION +
          '/dist/leaflet.css';
        link.setAttribute('data-wonderly-dz-leaflet', '');
        document.head.appendChild(link);
      }

      const script = document.createElement('script');
      script.src =
        'https://unpkg.com/leaflet@' + LEAFLET_VERSION + '/dist/leaflet.js';
      script.async = true;
      script.onload = function () {
        if (window.L && window.L.map) {
          resolve(window.L);
        } else {
          reject(new Error('Leaflet failed to initialize.'));
        }
      };
      script.onerror = function () {
        window._wonderlyDzLeafletLoading = null;
        reject(new Error('Leaflet script failed to load.'));
      };
      document.head.appendChild(script);
    });

    return window._wonderlyDzLeafletLoading;
  }

  function geocodeZipNominatim(zip, country) {
    const params = new URLSearchParams({
      postalcode: zip,
      countrycodes: country.toLowerCase(),
      format: 'json',
      limit: '1',
    });

    return fetch(
      'https://nominatim.openstreetmap.org/search?' + params.toString(),
      {
        headers: {
          Accept: 'application/json',
        },
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Geocode request failed.');
        }

        return response.json();
      })
      .then(function (results) {
        if (!results || !results[0]) {
          return null;
        }

        return {
          lat: parseFloat(results[0].lat),
          lng: parseFloat(results[0].lon),
        };
      })
      .catch(function () {
        return null;
      });
  }

  function makeLeafletPin(L, color, height) {
    const width = Math.round(height * 0.72);

    return L.divIcon({
      className: 'wonderly-delivery-zone__leaflet-pin-icon',
      html:
        '<svg xmlns="http://www.w3.org/2000/svg" width="' +
        width +
        '" height="' +
        height +
        '" viewBox="0 0 24 24" aria-hidden="true"><path d="' +
        PIN_PATH +
        '" fill="' +
        color +
        '" stroke="#222222" stroke-width="1"/></svg>',
      iconSize: [width, height],
      iconAnchor: [width / 2, height],
      popupAnchor: [0, -height + 4],
    });
  }

  function mapLoadErrorMessage(root, detail) {
    const base =
      root.dataset.mapLoadError ||
      'Map could not load. Pickup locations are still listed below.';
    const localHint = root.dataset.mapLocalDevHint || '';

    if (isLocalDevHost() && localHint) {
      return base + ' ' + localHint;
    }

    if (detail) {
      return base + ' (' + detail + ')';
    }

    return base;
  }

  function googleMapsReady() {
    return window.google && window.google.maps && window.google.maps.Map;
  }

  function chainGmAuthFailure(onFailure) {
    const prior = window.gm_authFailure;

    window.gm_authFailure = function () {
      window._wonderlyDzMapsAuthFailed = true;
      onFailure();
      if (typeof prior === 'function' && prior !== window.gm_authFailure) {
        prior();
      }
    };
  }

  function loadGoogleMaps(config, root, timeoutMs) {
    const timeout = timeoutMs || 20000;
    const apiKey = getMapsApiKey(root, config);

    if (!apiKey) {
      return Promise.reject(new Error('Google Maps API key not available from Zapiet.'));
    }

    const useExistingMaps =
      googleMapsReady() &&
      !window._wonderlyDzMapsAuthFailed &&
      (!root.dataset.googleMapsApiKey || window._wonderlyDzMapsKeyUsed === apiKey);

    if (useExistingMaps) {
      return Promise.resolve(window.google.maps);
    }

    if (window._wonderlyDzMapsLoading) {
      return window._wonderlyDzMapsLoading;
    }

    window._wonderlyDzMapsAuthFailed = false;

    window._wonderlyDzMapsLoading = new Promise(function (resolve, reject) {
      const timer = window.setTimeout(function () {
        reject(new Error('Google Maps timed out.'));
      }, timeout);

      function failAuth() {
        window.clearTimeout(timer);
        window._wonderlyDzMapsLoading = null;
        reject(
          new Error(
            'Google Maps authentication failed (RefererNotAllowedMapError).'
          )
        );
      }

      chainGmAuthFailure(failAuth);

      window[MAP_CALLBACK] = function () {
        if (window._wonderlyDzMapsAuthFailed) {
          return;
        }

        window.clearTimeout(timer);
        delete window[MAP_CALLBACK];
        window._wonderlyDzMapsLoading = null;
        window._wonderlyDzMapsKeyUsed = apiKey;

        if (googleMapsReady()) {
          resolve(window.google.maps);
        } else {
          reject(new Error('Google Maps failed to initialize.'));
        }
      };

      const script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(apiKey) +
        '&v=weekly&loading=async&callback=' +
        MAP_CALLBACK;
      script.async = true;
      script.defer = true;
      script.onerror = function () {
        window.clearTimeout(timer);
        window._wonderlyDzMapsLoading = null;
        reject(new Error('Google Maps script failed to load.'));
      };
      document.head.appendChild(script);
    });

    return window._wonderlyDzMapsLoading;
  }

  function waitForGoogleMaps(config, root, timeoutMs) {
    const pollMs = 2500;
    const timeout = timeoutMs || 20000;
    const start = Date.now();
    const overrideKey = (root && root.dataset.googleMapsApiKey) || '';

    return new Promise(function (resolve, reject) {
      function poll() {
        const canUseExisting =
          googleMapsReady() &&
          !window._wonderlyDzMapsAuthFailed &&
          (!overrideKey || window._wonderlyDzMapsKeyUsed === overrideKey.trim());

        if (canUseExisting) {
          resolve(window.google.maps);
          return;
        }

        if (Date.now() - start > pollMs) {
          loadGoogleMaps(config, root, timeout).then(resolve).catch(reject);
          return;
        }

        requestAnimationFrame(poll);
      }

      poll();
    });
  }

  function verifyMapTiles(root, map, maps, onFailure) {
    let tilesLoaded = false;

    maps.event.addListenerOnce(map, 'tilesloaded', function () {
      tilesLoaded = true;
    });

    window.setTimeout(function () {
      if (!tilesLoaded || window._wonderlyDzMapsAuthFailed) {
        onFailure();
      }
    }, 4000);
  }

  function deliveryLocationsUrl(config, zip) {
    const shop = window.Shopify.shop;
    const query = encodeURIComponent(zip);
    const isUs = config.api_region === 'api-us';

    if (isUs) {
      return (
        'https://api-us.zapiet.com/v1.0/delivery/locations?shop=' +
        shop +
        '&geoSearchQuery=' +
        query
      );
    }

    return (
      'https://api.zapiet.com/v4.0/delivery/locations?shop=' +
      shop +
      '&geoSearchQuery=' +
      query
    );
  }

  function pickupLocationsUrl(zip) {
    const shop = window.Shopify.shop;
    const query = encodeURIComponent(zip);

    return (
      'https://api-us.zapiet.com/v1.0/pickup/locations?shop=' +
      shop +
      '&geoSearchQuery=' +
      query
    );
  }

  function fetchDeliveryEligibility(config, zip) {
    return fetch(deliveryLocationsUrl(config, zip))
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        return {
          eligible: !data.error,
          location: data.error ? null : data,
          raw: data,
        };
      });
  }

  function fetchPickupLocations(_config, zip) {
    return fetch(pickupLocationsUrl(zip))
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        const locations = Array.isArray(data.locations) ? data.locations : [];

        return {
          eligible: locations.length > 0,
          locations: locations,
          raw: data,
        };
      });
  }

  function checkAvailability(config, zip) {
    return Promise.all([
      fetchDeliveryEligibility(config, zip),
      fetchPickupLocations(config, zip),
    ]).then(function (results) {
      return {
        zip: zip,
        delivery: results[0],
        pickup: results[1],
      };
    });
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatAddress(location) {
    const parts = [
      location.address_line_1,
      location.city,
      location.region,
      location.postal_code,
    ].filter(Boolean);

    return parts.join(', ');
  }

  function formatDistance(distance, unitLabel) {
    if (distance == null || Number.isNaN(Number(distance))) return '';

    return Number(distance).toFixed(1) + ' ' + unitLabel;
  }

  function makePinIcon(maps, color, scale) {
    return {
      path: PIN_PATH,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#222222',
      strokeWeight: 1,
      scale: scale || 1.4,
      anchor: new maps.Point(12, 22),
    };
  }

  function resetMapElement(mapEl) {
    if (!mapEl) return;

    mapEl.hidden = false;
    mapEl.innerHTML = '';
    mapEl.className = 'wonderly-delivery-zone__map';
    mapEl.removeAttribute('style');
  }

  function clearMap(root) {
    const instance = root._dzMap;
    if (!instance) return;

    if (instance.provider === 'leaflet') {
      try {
        instance.map.remove();
      } catch (e) {
        /* Leaflet may already be torn down */
      }
    } else {
      instance.markers.forEach(function (marker) {
        marker.setMap(null);
      });

      if (instance.infoWindow) {
        instance.infoWindow.close();
      }
    }

    root._dzMap = null;
  }

  function showMapFallback(root, message) {
    const mapEl = root.querySelector('[data-dz-map]');
    const fallbackEl = root.querySelector('[data-dz-map-fallback]');

    if (mapEl) mapEl.hidden = true;
    if (fallbackEl) {
      fallbackEl.hidden = false;
      fallbackEl.textContent = message;
    }
  }

  function getMapRenderContext(root, result, config) {
    const country = ((config && config.base_country_code) || 'US').toLowerCase();
    const pinColor =
      getComputedStyle(root).getPropertyValue('--wonderly-dz-map-pin').trim() ||
      root.dataset.mapPinColor ||
      '#fd3d00';
    const locations = (result.pickup && result.pickup.locations) || [];

    return {
      country: country,
      pinColor: pinColor,
      customerLabel: root.dataset.mapCustomerLabel || 'Your search area',
      geocodeError: root.dataset.mapGeocodeError || 'Map unavailable for this ZIP.',
      validLocations: locations.filter(function (location) {
        return location.latitude && location.longitude;
      }),
    };
  }

  function locationPopupHtml(location) {
    return (
      '<div class="wonderly-delivery-zone__map-info">' +
      '<strong>' +
      escapeHtml(location.company_name) +
      '</strong><br>' +
      escapeHtml(formatAddress(location)) +
      '</div>'
    );
  }

  function renderLeafletMap(root, result, config) {
    const mapEl = root.querySelector('[data-dz-map]');
    const fallbackEl = root.querySelector('[data-dz-map-fallback]');

    if (!mapEl) return Promise.resolve();

    clearMap(root);
    resetMapElement(mapEl);

    const ctx = getMapRenderContext(root, result, config);

    return loadLeaflet()
      .then(function (L) {
        return geocodeZipNominatim(result.zip, ctx.country).then(function (customerLatLng) {
          if (!customerLatLng && !ctx.validLocations.length) {
            showMapFallback(root, ctx.geocodeError);
            return;
          }

          const map = L.map(mapEl, {
            scrollWheelZoom: false,
          });

          L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          }).addTo(map);

          const bounds = L.latLngBounds([]);
          const markers = [];

          if (customerLatLng) {
            const customerMarker = L.marker([customerLatLng.lat, customerLatLng.lng], {
              icon: makeLeafletPin(L, '#222222', 26),
              title: ctx.customerLabel,
            }).addTo(map);

            customerMarker.bindTooltip(ctx.customerLabel);
            bounds.extend([customerLatLng.lat, customerLatLng.lng]);
            markers.push(customerMarker);
          }

          ctx.validLocations.forEach(function (location, index) {
            const lat = Number(location.latitude);
            const lng = Number(location.longitude);
            const isClosest = index === 0;
            const marker = L.marker([lat, lng], {
              icon: makeLeafletPin(L, ctx.pinColor, isClosest ? 34 : 28),
              title: location.company_name || '',
              zIndexOffset: isClosest ? 900 : 100 + index,
            }).addTo(map);

            marker.bindPopup(locationPopupHtml(location));

            if (isClosest) {
              marker.openPopup();
            }

            bounds.extend([lat, lng]);
            markers.push(marker);
          });

          if (bounds.isValid()) {
            if (markers.length === 1) {
              map.setView(bounds.getCenter(), 11);
            } else {
              map.fitBounds(bounds, { padding: [32, 32] });
            }
          }

          root._dzMap = {
            provider: 'leaflet',
            map: map,
            markers: markers,
          };

          if (fallbackEl) fallbackEl.hidden = true;

          window.setTimeout(function () {
            map.invalidateSize();
            if (bounds.isValid() && markers.length > 1) {
              map.fitBounds(bounds, { padding: [32, 32] });
            }
          }, 250);
        });
      })
      .catch(function () {
        showMapFallback(root, mapLoadErrorMessage(root));
      });
  }

  function renderGoogleMap(root, result, maps, config) {
    const mapEl = root.querySelector('[data-dz-map]');
    const fallbackEl = root.querySelector('[data-dz-map-fallback]');

    if (!mapEl) return Promise.resolve();

    clearMap(root);
    resetMapElement(mapEl);

    const ctx = getMapRenderContext(root, result, config);

    function buildMap(customerLatLng) {
      if (!customerLatLng && !ctx.validLocations.length) {
        showMapFallback(root, ctx.geocodeError);
        return;
      }

      const map = new maps.Map(mapEl, {
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: maps.ControlPosition.TOP_LEFT,
        },
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
      });

      const bounds = new maps.LatLngBounds();
      const markers = [];
      const infoWindow = new maps.InfoWindow();

      if (customerLatLng) {
        const customerMarker = new maps.Marker({
          map: map,
          position: customerLatLng,
          title: ctx.customerLabel,
          icon: makePinIcon(maps, '#222222', 1.3),
          zIndex: 1000,
        });
        markers.push(customerMarker);
        bounds.extend(customerLatLng);
      }

      ctx.validLocations.forEach(function (location, index) {
        const position = {
          lat: Number(location.latitude),
          lng: Number(location.longitude),
        };
        const isClosest = index === 0;
        const marker = new maps.Marker({
          map: map,
          position: position,
          title: location.company_name || '',
          icon: makePinIcon(maps, ctx.pinColor, isClosest ? 1.7 : 1.35),
          zIndex: isClosest ? 900 : 100 + index,
        });

        const content = locationPopupHtml(location);

        marker.addListener('click', function () {
          infoWindow.setContent(content);
          infoWindow.open(map, marker);
        });

        markers.push(marker);
        bounds.extend(position);

        if (isClosest) {
          infoWindow.setContent(content);
          infoWindow.open(map, marker);
        }
      });

      if (bounds.isEmpty()) {
        showMapFallback(root, ctx.geocodeError);
        return;
      }

      if (markers.length === 1) {
        map.setCenter(bounds.getCenter());
        map.setZoom(11);
      } else {
        map.fitBounds(bounds, 56);
      }

      root._dzMap = {
        provider: 'google',
        map: map,
        markers: markers,
        infoWindow: infoWindow,
      };

      mapEl.hidden = false;
      if (fallbackEl) fallbackEl.hidden = true;

      verifyMapTiles(root, map, maps, function () {
        clearMap(root);
        renderLeafletMap(root, result, config);
      });

      window.setTimeout(function () {
        maps.event.trigger(map, 'resize');
        if (markers.length > 1) {
          map.fitBounds(bounds, 56);
        }
      }, 250);
    }

    return new Promise(function (resolve) {
      if (!window.google || !window.google.maps) {
        renderLeafletMap(root, result, config).then(resolve);
        return;
      }

      const geocoder = new maps.Geocoder();

      geocoder.geocode(
        {
          address: result.zip,
          componentRestrictions: { country: ctx.country },
        },
        function (geoResults, status) {
          let customerLatLng = null;

          if (status === 'OK' && geoResults && geoResults[0]) {
            customerLatLng = geoResults[0].geometry.location;
          }

          buildMap(customerLatLng);
          resolve();
        }
      );
    });
  }

  function renderResultsMap(root, result, config) {
    return new Promise(function (resolve) {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          if (hasMerchantGoogleKey(root)) {
            waitForGoogleMaps(config, root)
              .then(function (maps) {
                return renderGoogleMap(root, result, maps, config);
              })
              .catch(function () {
                return renderLeafletMap(root, result, config);
              })
              .then(resolve);
            return;
          }

          renderLeafletMap(root, result, config).then(resolve);
        });
      });
    });
  }

  function setState(root, state) {
    root.dataset.state = state;
    root.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    root.querySelectorAll('[data-dz-panel]').forEach(function (panel) {
      panel.hidden = panel.dataset.dzPanel !== state;
    });
  }

  function renderResultCopy(root, result) {
    const headingEl = root.querySelector('[data-dz-result-heading]');
    const bodyEl = root.querySelector('[data-dz-result-body]');

    if (headingEl) {
      headingEl.textContent = result.delivery.eligible
        ? root.dataset.deliveryYesHeading || 'Good news! We deliver to your area.'
        : root.dataset.deliveryNoHeading || 'Ah, the rotten egg.';
    }

    if (bodyEl) {
      bodyEl.textContent = result.delivery.eligible
        ? root.dataset.deliveryYesBody || ''
        : root.dataset.deliveryNoBody || '';
    }
  }

  function renderPickupTable(root, pickup, labels) {
    const tableWrap = root.querySelector('[data-dz-pickup-table-wrap]');
    const tbody = root.querySelector('[data-dz-pickup-tbody]');

    if (!tableWrap || !tbody) return;

    tbody.textContent = '';

    if (!pickup.eligible || !pickup.locations.length) {
      tableWrap.hidden = true;
      return;
    }

    tableWrap.hidden = false;

    pickup.locations.forEach(function (location, index) {
      const row = document.createElement('tr');
      const isClosest = index === 0;

      row.className = isClosest
        ? 'wonderly-delivery-zone__pickup-row wonderly-delivery-zone__pickup-row--closest'
        : 'wonderly-delivery-zone__pickup-row';

      const nameCell = document.createElement('td');
      nameCell.className = 'wonderly-delivery-zone__pickup-name';
      nameCell.textContent = location.company_name || '';

      if (isClosest && labels.closestBadge) {
        const badge = document.createElement('span');
        badge.className = 'wonderly-delivery-zone__pickup-badge';
        badge.textContent = labels.closestBadge;
        nameCell.appendChild(document.createElement('br'));
        nameCell.appendChild(badge);
      }

      const addressCell = document.createElement('td');
      addressCell.className = 'wonderly-delivery-zone__pickup-address';
      addressCell.textContent = formatAddress(location);

      const distanceCell = document.createElement('td');
      distanceCell.className = 'wonderly-delivery-zone__pickup-distance';
      distanceCell.textContent = formatDistance(location.distance, labels.distanceUnit);

      row.appendChild(nameCell);
      row.appendChild(addressCell);
      row.appendChild(distanceCell);
      tbody.appendChild(row);
    });
  }

  function getLabels(root) {
    return {
      closestBadge: root.dataset.pickupClosestBadge || 'Closest to you',
      distanceUnit: root.dataset.distanceUnit || 'mi',
    };
  }

  function renderResults(root, result) {
    renderResultCopy(root, result);

    root.dataset.deliveryResult = result.delivery.eligible ? 'yes' : 'no';

    const shopCta = root.querySelector('[data-dz-shop-cta]');
    const pickupCta = root.querySelector('[data-dz-pickup-cta]');

    if (shopCta) {
      shopCta.hidden = !result.delivery.eligible;
    }

    if (pickupCta) {
      pickupCta.hidden = result.delivery.eligible;
    }

    if (root.dataset.showPickupTable === 'true') {
      renderPickupTable(root, result.pickup, getLabels(root));
    } else {
      const tableWrap = root.querySelector('[data-dz-pickup-table-wrap]');
      if (tableWrap) {
        tableWrap.hidden = true;
      }
    }
  }

  function init(root) {
    const form = root.querySelector('[data-dz-form]');
    const input = root.querySelector('[data-dz-zip]');
    const errorEl = root.querySelector('[data-dz-error]');
    const debugEl = root.querySelector('[data-dz-debug]');
    const showDebug = root.dataset.showDebug === 'true';

    if (!form || !input) return;

    input.addEventListener('input', function () {
      if (window.WonderlyZipCheck) {
        input.value = window.WonderlyZipCheck.sanitizeInput(input.value);
      }
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();

      const zip = window.WonderlyZipCheck
        ? window.WonderlyZipCheck.normalize(input.value)
        : (input.value || '').trim();

      if (!zip) {
        input.focus();
        return;
      }

      if (window.WonderlyZipCheck && !window.WonderlyZipCheck.isValid(zip)) {
        if (errorEl) {
          errorEl.textContent =
            root.dataset.invalidZipMessage || 'Please enter a valid 5-digit ZIP Code.';
        }
        input.focus();
        return;
      }

      if (errorEl) errorEl.textContent = '';
      if (debugEl) debugEl.textContent = '';
      clearMap(root);
      setState(root, 'loading');

      let zapietConfig;

      waitForZapietConfig()
        .then(function (config) {
          zapietConfig = config;
          return checkAvailability(config, zip);
        })
        .then(function (result) {
          renderResults(root, result);
          setState(root, 'results');

          if (showDebug && debugEl) {
            try {
              debugEl.textContent = JSON.stringify(result, null, 2);
            } catch (e) {
              debugEl.textContent = String(result);
            }
          }

          root._dzLastResult = result;
          root._dzZapietConfig = zapietConfig;

          return renderResultsMap(root, result, zapietConfig);
        })
        .catch(function (err) {
          const isMapOnly =
            err &&
            err.message &&
            (err.message.indexOf('Google Maps') !== -1 ||
              err.message.indexOf('Referer') !== -1 ||
              err.message.indexOf('Leaflet') !== -1 ||
              err.message.indexOf('localhost') !== -1);

          if (isMapOnly && root.dataset.state === 'results' && root._dzLastResult) {
            renderLeafletMap(root, root._dzLastResult, root._dzZapietConfig);
            if (showDebug && debugEl) {
              debugEl.textContent = String(err.stack || err.message || err);
            }
            return;
          }

          if (errorEl) {
            errorEl.textContent =
              err && err.message ? err.message : 'Something went wrong. Please try again.';
          }
          if (showDebug && debugEl && err) {
            debugEl.textContent = String(err.stack || err.message || err);
          }
          setState(root, 'form');
        });
    });

    root.querySelectorAll('[data-dz-reset]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (errorEl) errorEl.textContent = '';
        if (debugEl) debugEl.textContent = '';
        clearMap(root);
        delete root.dataset.deliveryResult;
        setState(root, 'form');
        input.focus();
      });
    });
  }

  function boot() {
    if (!window._wonderlyDzGmAuthHooked) {
      window._wonderlyDzGmAuthHooked = true;
      chainGmAuthFailure(function () {});
    }

    document.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
